<script setup lang="ts">
import type { KDropdownItem } from "@kaleidoscope-solutions/ks-design-system"
import {
  KDropdown,
  KSelectWrapper,
} from "@kaleidoscope-solutions/ks-design-system"

import { QuestionIcon } from "@kaleidoscope-solutions/ks-icons/vue/fill"
import {
  ArrowLongRightIcon,
  ArrowRightIcon,
} from "@kaleidoscope-solutions/ks-icons/vue/light"
import { computed } from "vue"
import LocateNotInFileDropdown from "@/components/FileImport/Locate/LocateNotInFileDropdown.vue"
import LocateDropdownItem from "@/components/FileImport/LocateDropdownItem.vue"
import StatusDecoration from "@/components/FileImport/StatusDecoration.vue"
import { isColumnEmpty, useFileImportStore } from "@/stores/index.js"
import { NOT_IN_FILE } from "@/types/index.js"
import { ALPHA_UNIT_PRICE, ALPHA_WEIGHTING_COLUMN } from "@/utils/alphaModel"

type LocateItem = KDropdownItem & { id: number }

interface Props {
  locateItems: LocateItem[]
  selectedTableHeader?: number
  keyTypes?: string[]
}

interface Emits {
  (event: "update:selectedTableHeader", value: any): void
  (event: "clearSelectedTableHeader"): void
}

const props = defineProps<Props>()

const emit = defineEmits<Emits>()

const fileImportStore = useFileImportStore()

function handleValueColumnFocus(index: number) {
  emit("update:selectedTableHeader", fileImportStore.valueColumns?.[index])
}

function clearSelectedTableHeader() {
  emit("clearSelectedTableHeader")
}

function handleValueColumnSelect(index: number, item: LocateItem) {
  emit("update:selectedTableHeader", item.id)
  fileImportStore.setLocateValueColumnByIndex(index, item.id)
}

function handleValueColumnClosed() {
  fileImportStore.locateValueInteraction.markTouched()
}

function handleKeyTypeFocus(keyType: string) {
  if (fileImportStore.locateInfo.get(keyType) === NOT_IN_FILE) {
    clearSelectedTableHeader()
    return
  }

  emit("update:selectedTableHeader", fileImportStore.locateInfo.get(keyType))
}

function handleKeyTypeSelect(categoryName: string, item: LocateItem) {
  emit("update:selectedTableHeader", item.id)
  fileImportStore.setLocateColumnForCategory(categoryName, item.id)
}

function isKeyTypeSelected(keyType: string) {
  if (fileImportStore.locateInfo.get(keyType) === NOT_IN_FILE) {
    return false
  }

  if (props.selectedTableHeader === undefined) {
    return false
  }

  return fileImportStore.locateInfo.get(keyType) === props.selectedTableHeader
}

function handleKeyTypeClosed(categoryName: string) {
  const notInFile = fileImportStore.locateInfo.get(categoryName) === NOT_IN_FILE

  if (!notInFile) {
    fileImportStore.locateColumnInteractions.markTouched(categoryName)
  }
}

function handleNotInFile(categoryName: string, close?: () => void) {
  clearSelectedTableHeader()
  fileImportStore.setLocateColumnForCategory(categoryName, NOT_IN_FILE)
  if (close !== undefined) {
    close()
  }
}

function handleNotInFileClosed(categoryName: string) {
  fileImportStore.locateColumnInteractions.markTouched(categoryName)
}

function handleNotInFileSelect(categoryName: string, value: string) {
  fileImportStore.setLocateNotInFileForCategory(categoryName, value)
}

function handleNotInFileCreate(categoryName: string, value: string) {
  fileImportStore.setLocateNotInFileForCategory(categoryName, value)
}

const keyTargets = computed(() => {
  return props.locateItems.map((item) => {
    const noValues = isColumnEmpty(item.id, fileImportStore.file!)
    const tooltip = noValues ? "No items found in this column" : undefined

    // Do not mutate the existing item as objects are passed by reference
    // and not shallow / deep cloned in a map
    return {
      ...item,
      disabled: noValues,
      tooltip,
    }
  })
})

function valueTooltip(
  noValues: boolean,
  invalidValues: boolean
): string | undefined {
  if (noValues) {
    return "No values found in this column"
  }

  if (invalidValues) {
    return "No numerical values were found in this column"
  }

  return undefined
}

const valueTargets = computed<KDropdownItem[]>(() => {
  return props.locateItems.map<KDropdownItem>((item) => {
    const noValues = isColumnEmpty(item.id, fileImportStore.file!)

    const invalidValues = fileImportStore.validTargetsInvalidValues?.[item.id].size > 0

    const tooltip = valueTooltip(noValues, invalidValues)

    return {
      ...item,
      disabled: noValues || invalidValues,
      tooltip,
    }
  })
})

const requiresWeighting = computed(() => {
  return fileImportStore.targets?.includes(ALPHA_UNIT_PRICE)
})
</script>

<template>
  <div class="flex flex-row items-center gap-2 pr-8">
    <template
      v-for="(target, index) in fileImportStore.targets"
      :key="target"
    >
      <KDropdown
        placeholder="Column to import"
        title="Select it from the file"
        :nullable="false"
        :model-value="fileImportStore.valueColumns?.[index]"
        :items="valueTargets"
        class="w-52 bg-white"
        @update:model-value="handleValueColumnSelect(index, $event)"
        @focusin="handleValueColumnFocus(index)"
        @closed="handleValueColumnClosed"
      >
        <template #selected="{ selected, open }">
          <div
            class="w-52 ring-teal-700 focus-within:ring-2 focus-visible:ring-2"
          >
            <KSelectWrapper
              :open="open"
              :is-selected="
                selectedTableHeader !== undefined
                  && fileImportStore.valueColumns?.[index] === selectedTableHeader
              "
            >
              <span
                v-if="selected !== undefined"
                class="font-bold"
              >
                {{ locateItems[selected]?.label }}
              </span>
              <span
                v-else
                class="text-gray-700"
              >
                Column to import
              </span>
            </KSelectWrapper>
          </div>
        </template>
        <template #item="{ item }">
          <LocateDropdownItem :item="item" />
        </template>
      </KDropdown>

      <ArrowLongRightIcon
        class="size-4 shrink-0 text-black"
      />

      <div
        class="flex w-full items-center gap-2"
      >
        <span>
          {{ target }}
        </span>

        <QuestionIcon
          v-if="fileImportStore.locateTooltips.has(`${target}`)"
          v-tooltip="fileImportStore.locateTooltips.get(`${target}`)"
          class="inline-block size-4 shrink-0"
        />

        <StatusDecoration :decorator="fileImportStore.locateValueColumnDecorator" />
      </div>
    </template>
  </div>

  <template v-if="keyTypes">
    <div
      v-for="keyType in keyTypes"
      :key="keyType"
      class="flex flex-row items-center gap-2 pr-8"
    >
      <KDropdown
        placeholder="Column to import"
        title="Select it from the file"
        :nullable="false"
        :model-value="fileImportStore.locateInfo.get(keyType)"
        :items="keyTargets"
        class="w-52 bg-white"
        @update:model-value="handleKeyTypeSelect(keyType, $event)"
        @focusin="handleKeyTypeFocus(keyType)"
        @closed="handleKeyTypeClosed(keyType)"
      >
        <template #selected="{ selected, open, focused }">
          <div
            class="w-52"
            :data-test="`locate-${keyType.replace(/ /g, '-')}-dropdown-btn`"
          >
            <KSelectWrapper
              :open="open"
              :is-selected="isKeyTypeSelected(keyType) && focused"
            >
              <span
                v-if="fileImportStore.locateInfo.get(keyType) === NOT_IN_FILE"
                class="font-bold"
              >
                Not in file
              </span>
              <span
                v-else-if="keyTargets[selected]?.label"
                class="font-bold"
              >
                {{ locateItems[selected]?.label }}
              </span>
              <span
                v-else
                class="text-gray-700"
              >
                Column to import
              </span>
            </KSelectWrapper>
          </div>
        </template>
        <template #item="{ item }">
          <LocateDropdownItem :item="item" />
        </template>
        <template #buttons="{ close }">
          <button
            class="flex w-full cursor-pointer items-center border-t border-t-warmgray-400 px-3 py-2 font-bold text-violet-500 hover:bg-gray-300"
            data-test="not-in-file-dropdown-btn"
            @click="handleNotInFile(keyType, close)"
          >
            <span class="pl-2">Not in the file</span>
            <ArrowRightIcon class="my-1 mr-3 ml-auto flex size-5" />
          </button>
        </template>
      </KDropdown>

      <LocateNotInFileDropdown
        v-if="fileImportStore.locateInfo.get(keyType) === NOT_IN_FILE"
        :model-value="fileImportStore.notInFileMap.get(keyType) ?? ''"
        :key-type="keyType"
        @update:model-value="handleNotInFileSelect(keyType, $event as string)"
        @focusin="clearSelectedTableHeader"
        @closed="handleNotInFileClosed(keyType)"
        @create="handleNotInFileCreate(keyType, $event as string)"
      >
        <template #selected="{ selected, open }">
          <div
            class="w-52"
            :data-test="`locate-${keyType.replace(/ /g, '-')}-not-in-file-dropdown-btn`"
          >
            <KSelectWrapper
              :open="open"
              class="font-bold"
            >
              {{
                selected
              }}
            </KSelectWrapper>
          </div>
        </template>
      </LocateNotInFileDropdown>

      <ArrowLongRightIcon
        class="size-4 shrink-0 text-black"
      />

      <span class="flex w-full text-base">{{ keyType }}</span>

      <QuestionIcon
        v-if="fileImportStore.locateTooltips.has(keyType)"
        v-tooltip="fileImportStore.locateTooltips.get(keyType)"
        class="size-4 shrink-0"
      />

      <StatusDecoration :decorator="fileImportStore.locateDecorator(keyType)" />
    </div>
  </template>

  <div
    v-if="requiresWeighting"
    class="flex flex-row items-center gap-2 pr-8"
  >
    <KDropdown
      placeholder="Column to import"
      title="Select it from the file"
      :nullable="false"
      :model-value="fileImportStore.weightingColumn"
      :items="valueTargets"
      class="w-52 bg-white"
      @update:model-value="fileImportStore.setWeightingColumn($event.id)"
    >
      <template #selected="{ selected, open }">
        <div
          class="w-52 ring-teal-700 focus-within:ring-2 focus-visible:ring-2"
        >
          <KSelectWrapper
            :open="open"
            :is-selected="
              selectedTableHeader !== undefined
                && fileImportStore.weightingColumn === selectedTableHeader
            "
          >
            <span
              v-if="selected !== undefined"
              class="font-bold"
            >
              {{ locateItems[selected]?.label }}
            </span>
            <span
              v-else
              class="text-gray-700"
            >
              Column to import
            </span>
          </KSelectWrapper>
        </div>
      </template>
      <template #item="{ item }">
        <LocateDropdownItem :item="item" />
      </template>
    </KDropdown>

    <ArrowLongRightIcon
      class="size-4 shrink-0 text-black"
    />

    <div
      class="flex w-full items-center gap-2"
    >
      <span>
        Weighting Column
      </span>

      <QuestionIcon
        v-if="fileImportStore.locateTooltips.has(ALPHA_WEIGHTING_COLUMN)"
        v-tooltip="fileImportStore.locateTooltips.get(ALPHA_WEIGHTING_COLUMN)"
        class="inline-block size-4 shrink-0"
      />

      <StatusDecoration :decorator="fileImportStore.locateValueColumnDecorator" />
    </div>
  </div>
</template>
